/**
 * POS Health Guards
 *
 * Guards for checking POS terminal reader health status.
 * Used to gate payment flow entry and determine fallback paths.
 */

import type { GuardWithMetadata } from "./sms.guard";

/**
 * Is POS Healthy Guard
 *
 * Returns true if the POS reader is healthy and ready for payments.
 * Used to gate entry into POS payment flow.
 */
export const isPOSHealthy: GuardWithMetadata = {
  impl: ({ context }) => {
    return context.posHealthStatus === "healthy";
  },
  metadata: {
    displayName: "isPOSHealthy",
    description: "Check if POS reader is healthy and ready for payments",
    requiresContext: ["posHealthStatus"],
    category: "context",
  },
};

/**
 * Is POS Degraded Guard
 *
 * Returns true if the POS reader is in a degraded state.
 * May still work but with potential issues.
 */
export const isPOSDegraded: GuardWithMetadata = {
  impl: ({ context }) => {
    return context.posHealthStatus === "degraded";
  },
  metadata: {
    displayName: "isPOSDegraded",
    description: "Check if POS reader is in degraded state",
    requiresContext: ["posHealthStatus"],
    category: "context",
  },
};

/**
 * Is POS Offline Guard
 *
 * Returns true if the POS reader is offline or unavailable.
 */
export const isPOSOffline: GuardWithMetadata = {
  impl: ({ context }) => {
    return context.posHealthStatus === "offline";
  },
  metadata: {
    displayName: "isPOSOffline",
    description: "Check if POS reader is offline",
    requiresContext: ["posHealthStatus"],
    category: "context",
  },
};

/**
 * Is POS Available Guard
 *
 * Returns true if the POS reader is available for use (healthy or degraded).
 * Degraded readers may still work, just with potential latency.
 */
export const isPOSAvailable: GuardWithMetadata = {
  impl: ({ context }) => {
    return (
      context.posHealthStatus === "healthy" ||
      context.posHealthStatus === "degraded"
    );
  },
  metadata: {
    displayName: "isPOSAvailable",
    description: "Check if POS reader is available (healthy or degraded)",
    requiresContext: ["posHealthStatus"],
    category: "context",
  },
};

/**
 * Is POS Unavailable Guard
 *
 * Returns true if the POS reader is not available (offline or unknown).
 * Used to determine if fallback to manual card entry is needed.
 */
export const isPOSUnavailable: GuardWithMetadata = {
  impl: ({ context }) => {
    return (
      context.posHealthStatus === "offline" ||
      context.posHealthStatus === "unknown" ||
      context.posHealthStatus === undefined
    );
  },
  metadata: {
    displayName: "isPOSUnavailable",
    description: "Check if POS reader is unavailable (offline or unknown)",
    requiresContext: ["posHealthStatus"],
    category: "context",
  },
};

/**
 * Has POS Reader Configured Guard
 *
 * Returns true if a POS reader ID is configured in context.
 * This is separate from health status - a reader can be configured but offline.
 */
export const hasPOSReaderConfigured: GuardWithMetadata = {
  impl: ({ context }) => {
    const readerId = context.stripeReaderId as string | undefined;
    return !!readerId && readerId.trim().length > 0;
  },
  metadata: {
    displayName: "hasPOSReaderConfigured",
    description: "Check if a POS reader ID is configured",
    requiresContext: ["stripeReaderId"],
    category: "context",
  },
};

/**
 * Should Use POS Payment Guard
 *
 * Composite guard that returns true if:
 * 1. Not in QR mode (QR mode uses web form)
 * 2. POS reader is configured
 * 3. POS reader is available (healthy or degraded)
 */
export const shouldUsePOSPayment: GuardWithMetadata = {
  impl: ({ context }) => {
    const isQRMode = context.qrMode === true;
    const readerId = context.stripeReaderId as string | undefined;
    const hasReader = !!readerId && readerId.trim().length > 0;
    const isAvailable =
      context.posHealthStatus === "healthy" ||
      context.posHealthStatus === "degraded";

    return !isQRMode && hasReader && isAvailable;
  },
  metadata: {
    displayName: "shouldUsePOSPayment",
    description:
      "Check if POS payment should be used (not QR, reader configured, reader available)",
    requiresContext: ["qrMode", "stripeReaderId", "posHealthStatus"],
    category: "context",
  },
};

/**
 * Should Fallback To Manual Entry Guard
 *
 * Returns true if payment should fall back to manual card entry.
 * This happens when:
 * 1. Not in QR mode (QR mode already uses web form)
 * 2. POS reader is configured but unavailable
 */
export const shouldFallbackToManualEntry: GuardWithMetadata = {
  impl: ({ context }) => {
    const isQRMode = context.qrMode === true;
    const readerId = context.stripeReaderId as string | undefined;
    const hasReader = !!readerId && readerId.trim().length > 0;
    const isUnavailable =
      context.posHealthStatus === "unknown" ||
      context.posHealthStatus === undefined;

    // Fallback needed when: not QR mode, has reader configured, but reader is unavailable
    return !isQRMode && hasReader && isUnavailable;
  },
  metadata: {
    displayName: "shouldFallbackToManualEntry",
    description:
      "Check if payment should fall back to manual card entry due to POS unavailability",
    requiresContext: ["qrMode", "stripeReaderId", "posHealthStatus"],
    category: "context",
  },
};
